import React, { useRef, useEffect, useState, memo, useMemo, useCallback } from 'react';
import { FixedSizeGrid, FixedSizeList, GridChildComponentProps, ListChildComponentProps } from 'react-window';
import { FileStat, FolderSizeResult } from '../../api/IFileSystem';
import { FileItem } from './FileItem';
import { PercentBar } from '../UI/PercentBar';
import { formatSize, formatDate } from '../../utils/fileUtils';
import { t } from '../../i18n';
import { useFileSearchStatusStore } from '../../store/useFileSearchStatus';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { FileSearchStatus } from '../../../../types';

// Constants - match regular grid layout (gap-2 = 8px between items)
const GRID_ITEM_WIDTH = 118;   // wider to match regular grid's auto-fill behavior
const GRID_ITEM_HEIGHT = 130;  // enough height to prevent overlap
const LIST_ITEM_HEIGHT = 36;   // slightly taller to accommodate percent bar
const LIST_HEADER_HEIGHT = 32; // height of list header

interface VirtualFileListProps {
  files: FileStat[];
  viewMode: 'grid' | 'list';
  selectedPaths: string[];
  renamingPath: string | null;
  activeDragFile: FileStat | null;
  folderSizes: Record<string, FolderSizeResult>;
  currentFolderSize: FolderSizeResult | null;
  fetchFolderSizes: (folders: FileStat[]) => void;
  onItemClick: (path: string, e: React.MouseEvent) => void;
  onItemDoubleClick: (file: FileStat) => void;
  onItemContextMenu: (e: React.MouseEvent, path: string) => void;
  onDropFiles: (files: any[], target: string) => void;
  renderRenameInput: (file: FileStat) => React.ReactNode;
}

// List header component
const ListHeader: React.FC = memo(() => (
  <div className="flex items-center px-4 py-2 border-b border-white/10 text-xs text-white/50 bg-black/20 sticky top-0 z-10">
    <div className="flex-1 min-w-0">{t('name')}</div>
    <div className="w-20 text-right shrink-0">{t('size')}</div>
    <div className="w-28 text-right shrink-0 hidden sm:block">{t('dateModified')}</div>
    <div className="w-24 shrink-0 pl-2 text-center">{t('parentPercent')}</div>
  </div>
));

// Memoized FileItem wrapper to prevent unnecessary re-renders
const MemoizedFileItem = memo(FileItem);

// Grid Cell Component with folder size support
const GridCell = memo(({
  columnIndex,
  rowIndex,
  style,
  data
}: GridChildComponentProps<{
  files: FileStat[];
  columnCount: number;
  selectedPaths: string[];
  renamingPath: string | null;
  activeDragFile: FileStat | null;
  folderSizes: Record<string, FolderSizeResult>;
  parentFolderSize: FolderSizeResult | null;
  isSearchEngineReady: boolean;
  searchEngineStatus: FileSearchStatus;
  openSearchEngineInitModal: (isReinit?: boolean) => void;
  onItemClick: (path: string, e: React.MouseEvent) => void;
  onItemDoubleClick: (file: FileStat) => void;
  onItemContextMenu: (e: React.MouseEvent, path: string) => void;
  onDropFiles: (files: any[], target: string) => void;
  renderRenameInput: (file: FileStat) => React.ReactNode;
}>) => {
  const { files, columnCount, selectedPaths, renamingPath, activeDragFile, folderSizes, parentFolderSize, isSearchEngineReady, searchEngineStatus, openSearchEngineInitModal, onItemClick, onItemDoubleClick, onItemContextMenu, onDropFiles, renderRenameInput } = data;
  const index = rowIndex * columnCount + columnIndex;

  if (index >= files.length) {
    return null;
  }

  const file = files[index];
  const isSelected = selectedPaths.includes(file.path);
  const isRenaming = renamingPath === file.path;
  const isGhost = activeDragFile && selectedPaths.includes(activeDragFile.path) &&
                  selectedPaths.includes(file.path) && file.path !== activeDragFile.path;
  const folderSize = file.type === 'dir' ? folderSizes[file.path] : null;

  return (
    <div style={style} className="p-[3px]">
      <MemoizedFileItem
        file={file}
        selected={isSelected}
        renaming={isRenaming}
        isGhost={!!isGhost}
        viewMode="grid"
        folderSize={folderSize}
        parentFolderSize={parentFolderSize}
        isSearchEngineReady={isSearchEngineReady}
        searchEngineStatus={searchEngineStatus}
        openSearchEngineInitModal={openSearchEngineInitModal}
        onClick={(e) => { e.stopPropagation(); onItemClick(file.path, e); }}
        onDoubleClick={() => onItemDoubleClick(file)}
        onContextMenu={(e) => { e.stopPropagation(); onItemContextMenu(e, file.path); }}
        onDropFiles={onDropFiles}
      >
        {isRenaming && renderRenameInput(file)}
      </MemoizedFileItem>
    </div>
  );
});

GridCell.displayName = 'GridCell';

// List Row Component with parent folder percentage
const ListRow = memo(({
  index,
  style,
  data
}: ListChildComponentProps<{
  files: FileStat[];
  selectedPaths: string[];
  renamingPath: string | null;
  activeDragFile: FileStat | null;
  folderSizes: Record<string, FolderSizeResult>;
  parentFolderSize: FolderSizeResult | null;
  isSearchEngineReady: boolean;
  searchEngineStatus: FileSearchStatus;
  openSearchEngineInitModal: (isReinit?: boolean) => void;
  onItemClick: (path: string, e: React.MouseEvent) => void;
  onItemDoubleClick: (file: FileStat) => void;
  onItemContextMenu: (e: React.MouseEvent, path: string) => void;
  onDropFiles: (files: any[], target: string) => void;
  renderRenameInput: (file: FileStat) => React.ReactNode;
}>) => {
  const { files, selectedPaths, renamingPath, activeDragFile, folderSizes, parentFolderSize, isSearchEngineReady, searchEngineStatus, openSearchEngineInitModal, onItemClick, onItemDoubleClick, onItemContextMenu, onDropFiles, renderRenameInput } = data;
  const file = files[index];
  const isSelected = selectedPaths.includes(file.path);
  const isRenaming = renamingPath === file.path;
  const isGhost = activeDragFile && selectedPaths.includes(activeDragFile.path) &&
                  selectedPaths.includes(file.path) && file.path !== activeDragFile.path;
  const folderSize = file.type === 'dir' ? folderSizes[file.path] : null;

  return (
    <div style={style}>
      <MemoizedFileItem
        file={file}
        selected={isSelected}
        renaming={isRenaming}
        isGhost={!!isGhost}
        viewMode="list"
        folderSize={folderSize}
        parentFolderSize={parentFolderSize}
        isSearchEngineReady={isSearchEngineReady}
        searchEngineStatus={searchEngineStatus}
        openSearchEngineInitModal={openSearchEngineInitModal}
        onClick={(e) => { e.stopPropagation(); onItemClick(file.path, e); }}
        onDoubleClick={() => onItemDoubleClick(file)}
        onContextMenu={(e) => { e.stopPropagation(); onItemContextMenu(e, file.path); }}
        onDropFiles={onDropFiles}
      >
        {isRenaming && renderRenameInput(file)}
      </MemoizedFileItem>
    </div>
  );
});

ListRow.displayName = 'ListRow';

// Hook to measure container size
function useContainerSize(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      setSize({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  return size;
}

// Main Virtual File List Component
export const VirtualFileList: React.FC<VirtualFileListProps> = ({
  files,
  viewMode,
  selectedPaths,
  renamingPath,
  activeDragFile,
  folderSizes,
  currentFolderSize,
  fetchFolderSizes,
  onItemClick,
  onItemDoubleClick,
  onItemContextMenu,
  onDropFiles,
  renderRenameInput,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<FixedSizeList>(null);
  const { width, height } = useContainerSize(containerRef);

  // Search engine status for "pending init" display
  const searchEngineStatus = useFileSearchStatusStore((s) => s.status);
  const openSearchEngineInitModal = useFileManagerStore((s) => s.openSearchEngineInitModal);
  const isSearchEngineReady = searchEngineStatus === FileSearchStatus.READY;

  // Account for padding (p-4 = 16px each side = 32px total)
  const PADDING = 32;
  const innerWidth = Math.max(0, width - PADDING);
  // For list mode, account for header height
  const listContentHeight = viewMode === 'list' ? Math.max(0, height - PADDING - LIST_HEADER_HEIGHT) : Math.max(0, height - PADDING);
  const gridContentHeight = Math.max(0, height - PADDING);

  // Calculate grid dimensions based on inner width (must be before callbacks that use it)
  const columnCount = Math.max(1, Math.floor(innerWidth / GRID_ITEM_WIDTH));
  const rowCount = Math.ceil(files.length / columnCount);

  // Callback to fetch folder sizes for visible items (list mode)
  const handleListItemsRendered = useCallback(({ visibleStartIndex, visibleStopIndex }: { visibleStartIndex: number; visibleStopIndex: number }) => {
    const visibleFolders = files.slice(visibleStartIndex, visibleStopIndex + 1).filter(f => f.type === 'dir');
    if (visibleFolders.length > 0) {
      fetchFolderSizes(visibleFolders);
    }
  }, [files, fetchFolderSizes]);

  // Callback to fetch folder sizes for visible items (grid mode)
  const handleGridItemsRendered = useCallback(({ visibleRowStartIndex, visibleRowStopIndex }: { visibleRowStartIndex: number; visibleRowStopIndex: number }) => {
    const startIndex = visibleRowStartIndex * columnCount;
    const stopIndex = Math.min((visibleRowStopIndex + 1) * columnCount - 1, files.length - 1);
    const visibleFolders = files.slice(startIndex, stopIndex + 1).filter(f => f.type === 'dir');
    if (visibleFolders.length > 0) {
      fetchFolderSizes(visibleFolders);
    }
  }, [files, columnCount, fetchFolderSizes]);

  // Memoize item data to prevent unnecessary re-renders
  const gridItemData = useMemo(() => ({
    files,
    columnCount,
    selectedPaths,
    renamingPath,
    activeDragFile,
    folderSizes,
    parentFolderSize: currentFolderSize,
    isSearchEngineReady,
    searchEngineStatus,
    openSearchEngineInitModal,
    onItemClick,
    onItemDoubleClick,
    onItemContextMenu,
    onDropFiles,
    renderRenameInput,
  }), [files, columnCount, selectedPaths, renamingPath, activeDragFile, folderSizes, isSearchEngineReady, searchEngineStatus, openSearchEngineInitModal, onItemClick, onItemDoubleClick, onItemContextMenu, onDropFiles, renderRenameInput]);

  const listItemData = useMemo(() => ({
    files,
    selectedPaths,
    renamingPath,
    activeDragFile,
    folderSizes,
    parentFolderSize: currentFolderSize,
    isSearchEngineReady,
    searchEngineStatus,
    openSearchEngineInitModal,
    onItemClick,
    onItemDoubleClick,
    onItemContextMenu,
    onDropFiles,
    renderRenameInput,
  }), [files, selectedPaths, renamingPath, activeDragFile, folderSizes, currentFolderSize, isSearchEngineReady, searchEngineStatus, openSearchEngineInitModal, onItemClick, onItemDoubleClick, onItemContextMenu, onDropFiles, renderRenameInput]);

  return (
    <div ref={containerRef} className="w-full h-full p-4">
      {innerWidth > 0 && (viewMode === 'list' ? listContentHeight : gridContentHeight) > 0 && (
        viewMode === 'grid' ? (
          <FixedSizeGrid
            columnCount={columnCount}
            columnWidth={GRID_ITEM_WIDTH}
            height={gridContentHeight}
            rowCount={rowCount}
            rowHeight={GRID_ITEM_HEIGHT}
            width={innerWidth}
            itemData={gridItemData}
            overscanRowCount={2}
            onItemsRendered={handleGridItemsRendered}
            className="scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
          >
            {GridCell}
          </FixedSizeGrid>
        ) : (
          <div className="flex flex-col h-full">
            <ListHeader />
            <FixedSizeList
              ref={listRef}
              height={listContentHeight}
              itemCount={files.length}
              itemSize={LIST_ITEM_HEIGHT}
              width={innerWidth}
              itemData={listItemData}
              overscanCount={5}
              onItemsRendered={handleListItemsRendered}
              className="scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
            >
              {ListRow}
            </FixedSizeList>
          </div>
        )
      )}
    </div>
  );
};

export default VirtualFileList;
